export interface CostCategory {
  label: string;
  vanLife: number;
  offGrid: number;
  boatLiving: number;
  camping: number;
}

export interface EquipmentItem {
  name: string;
  category: string;
  budget: number;
  mid: number;
  premium: number;
}

export const lifestyleLabels: Record<string, string> = {
  vanLife: 'Van Life',
  offGrid: 'Off-Grid Home',
  boatLiving: 'Boat Living',
  camping: 'Camping',
};

export const lifestyleColors: Record<string, string> = {
  vanLife: 'hsl(var(--primary))',
  offGrid: 'hsl(var(--accent))',
  boatLiving: '#4A90A4',
  camping: '#B87A3B',
};

export const monthlyCosts: CostCategory[] = [
  { label: 'Housing / Mooring', vanLife: 0, offGrid: 150, boatLiving: 450, camping: 0 },
  { label: 'Food & Groceries', vanLife: 320, offGrid: 280, boatLiving: 350, camping: 220 },
  { label: 'Insurance', vanLife: 145, offGrid: 85, boatLiving: 210, camping: 35 },
  { label: 'Maintenance', vanLife: 180, offGrid: 120, boatLiving: 280, camping: 45 },
  { label: 'Fuel / Energy', vanLife: 260, offGrid: 65, boatLiving: 190, camping: 80 },
  { label: 'Internet & Phone', vanLife: 75, offGrid: 70, boatLiving: 75, camping: 55 },
  { label: 'Other', vanLife: 110, offGrid: 90, boatLiving: 130, camping: 65 },
];

export const startupCosts: Record<string, number> = {
  vanLife: 18500,
  offGrid: 45000,
  boatLiving: 32000,
  camping: 3200,
};

export const budgetCategories = [
  'Vehicle / Property',
  'Conversion / Setup',
  'Solar & Electrical',
  'Water System',
  'Kitchen & Cooking',
  'Heating / Cooling',
  'Furniture & Storage',
  'Safety & Tools',
  'Internet & Tech',
  'Miscellaneous',
];

export const equipmentByLifestyle: Record<string, EquipmentItem[]> = {
  vanLife: [
    { name: 'Camper Van / Bus', category: 'Vehicle', budget: 5000, mid: 15000, premium: 45000 },
    { name: 'Solar Panel Kit (200W)', category: 'Electrical', budget: 280, mid: 550, premium: 1200 },
    { name: 'Leisure Battery (100Ah)', category: 'Electrical', budget: 120, mid: 300, premium: 650 },
    { name: 'Inverter (1000W)', category: 'Electrical', budget: 80, mid: 180, premium: 400 },
    { name: 'Water Tank (80L)', category: 'Water', budget: 45, mid: 90, premium: 180 },
    { name: 'Water Pump & Filter', category: 'Water', budget: 35, mid: 85, premium: 220 },
    { name: 'Portable Stove', category: 'Kitchen', budget: 30, mid: 90, premium: 250 },
    { name: 'Cooler / Mini Fridge', category: 'Kitchen', budget: 60, mid: 350, premium: 800 },
    { name: 'Insulation Kit', category: 'Build', budget: 150, mid: 400, premium: 900 },
    { name: 'Diesel Heater', category: 'Climate', budget: 120, mid: 300, premium: 700 },
    { name: 'Roof Vent / Fan', category: 'Climate', budget: 40, mid: 150, premium: 350 },
    { name: 'Bed / Mattress', category: 'Furniture', budget: 80, mid: 250, premium: 600 },
  ],
  offGrid: [
    { name: 'Land Plot', category: 'Property', budget: 8000, mid: 25000, premium: 60000 },
    { name: 'Tiny House / Cabin Shell', category: 'Structure', budget: 5000, mid: 18000, premium: 45000 },
    { name: 'Solar Array (1kW)', category: 'Electrical', budget: 1200, mid: 2800, premium: 6000 },
    { name: 'Battery Bank (5kWh)', category: 'Electrical', budget: 800, mid: 2500, premium: 5500 },
    { name: 'Well / Water Collection', category: 'Water', budget: 400, mid: 2000, premium: 6000 },
    { name: 'Composting Toilet', category: 'Sanitation', budget: 80, mid: 400, premium: 1200 },
    { name: 'Wood Stove', category: 'Climate', budget: 200, mid: 600, premium: 1800 },
    { name: 'Rainwater Filter System', category: 'Water', budget: 120, mid: 350, premium: 900 },
    { name: 'Basic Tools Set', category: 'Tools', budget: 150, mid: 400, premium: 900 },
    { name: 'Fencing / Security', category: 'Property', budget: 300, mid: 1200, premium: 3500 },
  ],
  boatLiving: [
    { name: 'Sailboat / Motorboat', category: 'Vessel', budget: 8000, mid: 25000, premium: 80000 },
    { name: 'Marine Solar (100W)', category: 'Electrical', budget: 200, mid: 500, premium: 1100 },
    { name: 'Marine Battery', category: 'Electrical', budget: 150, mid: 400, premium: 900 },
    { name: 'Anchor & Ground Tackle', category: 'Safety', budget: 100, mid: 350, premium: 800 },
    { name: 'Life Jackets & Safety Kit', category: 'Safety', budget: 80, mid: 200, premium: 500 },
    { name: 'Marine Radio (VHF)', category: 'Communication', budget: 60, mid: 180, premium: 400 },
    { name: 'Watermaker / Desalinator', category: 'Water', budget: 0, mid: 1500, premium: 4500 },
    { name: 'Dinghy / Tender', category: 'Transport', budget: 300, mid: 1200, premium: 3500 },
    { name: 'Marine Stove', category: 'Kitchen', budget: 80, mid: 300, premium: 800 },
    { name: 'Anti-fouling & Paint', category: 'Maintenance', budget: 150, mid: 400, premium: 900 },
  ],
  camping: [
    { name: 'Tent (3-season)', category: 'Shelter', budget: 80, mid: 250, premium: 600 },
    { name: 'Sleeping Bag (-5°C)', category: 'Shelter', budget: 40, mid: 120, premium: 350 },
    { name: 'Sleeping Pad', category: 'Shelter', budget: 25, mid: 80, premium: 200 },
    { name: 'Backpack (60L)', category: 'Gear', budget: 50, mid: 150, premium: 350 },
    { name: 'Camp Stove', category: 'Kitchen', budget: 25, mid: 70, premium: 180 },
    { name: 'Water Filter', category: 'Water', budget: 20, mid: 45, premium: 120 },
    { name: 'Headlamp & Lantern', category: 'Gear', budget: 15, mid: 40, premium: 100 },
    { name: 'First Aid Kit', category: 'Safety', budget: 15, mid: 40, premium: 90 },
    { name: 'Tarp / Rain Fly', category: 'Shelter', budget: 20, mid: 60, premium: 150 },
    { name: 'Portable Power Bank', category: 'Electrical', budget: 25, mid: 70, premium: 200 },
  ],
};

export const defaultRent = 1200;
